import { useEffect, useState } from 'react';
import { photoProofControllerServeV1 } from '@kidshome/api-contract';
import { cn } from '@kidshome/ui';

/**
 * An `<img>` for an AUTH-GATED image endpoint. A plain `<img src>` can't carry the `Authorization`
 * Bearer header, so the bytes are fetched through the shared axios instance (the serve function
 * returns the response as a `Blob`), turned into an object URL, and shown. The object URL is REVOKED
 * on unmount / key change so we never leak blob URLs.
 *
 * The fetcher is a prop (`fetcher`): each authed image route has its own generated serve function
 * (`GET /v1/tasks/photo-proof/:key`, `GET /v1/members/avatar/:key`, …). `AuthImageProof` /
 * `AuthImageAvatar` below bind the concrete route so call sites stay terse. While loading we show a
 * token-styled skeleton; on failure (e.g. a deleted object → 404) we render a small, non-shaming
 * placeholder instead of a broken image. The element keeps a real `alt` for assistive tech.
 */
export function AuthImage({
  imageKey,
  fetcher,
  alt,
  className,
  fallbackLabel,
  fallbackGlyph = '📷',
}: {
  /** The opaque image key (`ApprovalItemResponse.photoProofKey`, `MemberAvatarResponse.imageKey`, …). */
  imageKey: string;
  /** Route-specific auth-gated fetcher returning the image bytes as a `Blob`. */
  fetcher: (key: string, signal?: AbortSignal) => Promise<Blob>;
  alt: string;
  className?: string;
  /** Short label shown when the image can't be loaded (e.g. "Photo unavailable"). */
  fallbackLabel: string;
  /** Glyph shown on the fallback tile (defaults to a camera). */
  fallbackGlyph?: string;
}): React.ReactElement {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    let cancelled = false;
    setUrl(null);
    setFailed(false);

    fetcher(imageKey, controller.signal)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      controller.abort();
      // Revoke the object URL so the blob is released (no leak on unmount / key change).
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [imageKey, fetcher]);

  if (failed) {
    return (
      <div
        role="img"
        aria-label={fallbackLabel}
        className={cn(
          'flex items-center justify-center bg-surface-muted text-text-muted',
          className,
        )}
      >
        <span aria-hidden="true">{fallbackGlyph}</span>
      </div>
    );
  }

  if (!url) {
    return <div aria-hidden="true" className={cn('animate-pulse bg-surface-muted', className)} />;
  }

  return <img src={url} alt={alt} className={className} />;
}

/**
 * Module-level so the fetcher identity is stable across renders — otherwise a fresh inline closure
 * would re-run `AuthImage`'s `[imageKey, fetcher]` effect every render, re-downloading the bytes and
 * flashing the skeleton (mirrors `Avatar.tsx`'s `fetchMemberAvatar`).
 */
const fetchPhotoProof = (key: string, signal?: AbortSignal): Promise<Blob> =>
  photoProofControllerServeV1(key, undefined, signal);

/** The chore photo-proof thumbnail/lightbox (`GET /v1/tasks/photo-proof/:key`). */
export function AuthImageProof(
  props: Omit<React.ComponentProps<typeof AuthImage>, 'fetcher'>,
): React.ReactElement {
  return <AuthImage {...props} fetcher={fetchPhotoProof} />;
}
